import * as React from "react"
import { Col, Container, Hidden, Row, Visible } from "react-grid-system"
import { HorizontalSpacer, Spacer } from "../../atoms/spacers"
import SearchField from "../../atoms/inputs/SearchField"
import styled from "styled-components"
import IconButton from "../../atoms/buttons/IconButton"
import ListIcon from "../../atoms/icons/ListIcon"
import { theme } from "../../theme"
import DetailsIcon from "../../atoms/icons/DetailsIcon"
import PrimaryButton from "../../atoms/buttons/PrimaryButton"
import SecondaryButton from "../../atoms/buttons/SecondaryButton"
import { Link, useNavigate, useParams } from "react-router-dom"
import TerminalsOverviewCard from "../../organisms/TerminalsOverviewCard"
import { PAGINATION_VALUE, PAGINATION_VALUE_PRESETS } from "../../../utils/design-helpers"
import { formatApiTerminalsToTerminalModel } from "../../../utils/formatters"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import PaginationComponent from "../../molecules/PaginationComponent"
import Dropdown, { optionsMap } from "../../atoms/inputs/Dropdown"
import { useTranslation } from "react-i18next"
import { TerminalSortings } from "../../../utils/filter-helpers"
import { useAllTerminals } from "../../../hooks/useAllTerminals"
import { TerminalDataModel } from "../../../utils/models"
import ViewReportButton from "../../../reports/components/ViewReportButton"
import { terminalReportOptions } from "../../../reports/reports-helpers"
import EmptyDashboardPage from "./EmptyDashboardPage"
import NoSearchResultPage from "./NoSearchResultPage"
import { Key } from "react-aria-components"
import Button from "../../atoms/buttons/Button"

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 2rem;
`

interface Props {
  suborgFilter: Array<number> | undefined
}

const DROPDOWN_OPTIONS = [
  { id: TerminalSortings.terminalNameAsc, value: TerminalSortings.terminalNameAsc, name: "Name A-Z" },
  { id: TerminalSortings.terminalNameDesc, value: TerminalSortings.terminalNameDesc, name: "Name Z-A" },
  { id: TerminalSortings.dataPlanAsc, value: TerminalSortings.dataPlanAsc, name: "Data plan A-Z" },
  { id: TerminalSortings.dataPlanDesc, value: TerminalSortings.dataPlanDesc, name: "Data plan Z-A" },
  {
    id: TerminalSortings.subOrganizationAsc,
    value: TerminalSortings.subOrganizationAsc,
    name: "Suborganization A-Z",
  },
  {
    id: TerminalSortings.subOrganizationDesc,
    value: TerminalSortings.subOrganizationDesc,
    name: "Suborganization Z-A",
  },
  {
    id: TerminalSortings.serialNumberAsc,
    value: TerminalSortings.serialNumberAsc,
    name: "Serial number low - high",
  },
  {
    id: TerminalSortings.serialNumberDesc,
    value: TerminalSortings.serialNumberDesc,
    name: "Serial number high - low",
  },
  {
    id: TerminalSortings.dataUsageAsc,
    value: TerminalSortings.dataUsageAsc,
    name: "Data limit high - low",
  },
  {
    id: TerminalSortings.dataUsageDesc,
    value: TerminalSortings.dataUsageDesc,
    name: "Data limit low - high",
  },
]

const TerminalsPage: React.FunctionComponent<Props> = ({ suborgFilter }) => {
  const { t } = useTranslation()
  const { page } = useParams()
  const navigate = useNavigate()

  const [itemsPerPage, setItemsPerPage] = React.useState(PAGINATION_VALUE)
  const [cardsOpen, setCardsOpen] = React.useState<Array<string | number>>([])
  const [currentPage, setCurrentPage] = React.useState<number>(parseInt(page ?? "1"))
  const [offset, setoffset] = React.useState<number>(currentPage > 1 ? (currentPage - 1) * itemsPerPage : 0)
  const [searchValue, setSearchValue] = React.useState<undefined | string>(undefined)
  const [sortingId, setSortingId] = React.useState<Key>(TerminalSortings.terminalNameAsc)
  const [sortAsc, setSortAsc] = React.useState(true)

  const sorting = optionsMap(DROPDOWN_OPTIONS)[sortingId].value!

  //Fetch terminals
  const { allTerminals, isLoading } = useAllTerminals(itemsPerPage, offset, searchValue, suborgFilter, sorting)
  const [terminals, setTerminals] = React.useState<Array<TerminalDataModel>>([])
  const formattedTerminals = formatApiTerminalsToTerminalModel(allTerminals?.edges, suborgFilter?.length === 0)
  React.useMemo(() => {
    setTerminals(formattedTerminals ?? [])
  }, [allTerminals])

  //Handle open sections
  const checkCardIsOpen = (id: number) => {
    return cardsOpen.includes(id)
  }
  const onClickCard = (id: number) => {
    if (checkCardIsOpen(id)) {
      const arr = cardsOpen.filter((x) => x !== id)
      setCardsOpen(arr)
    } else {
      setCardsOpen([...cardsOpen, id])
    }
  }
  const onCloseCard = (id: number) => {
    if (checkCardIsOpen(id)) {
      const arr = cardsOpen.filter((x) => x !== id)
      setCardsOpen(arr)
    }
  }
  const onClickListView = () => {
    setCardsOpen([])
  }
  const allTerminalIds: Array<number> = []
  formattedTerminals?.map((t: any) => allTerminalIds.push(parseInt(t.id)))
  const onClickDetailsView = () => {
    setCardsOpen(allTerminalIds)
  }

  //Handle pagination
  const totalCount = allTerminals?.totalCount ?? 1
  const pagesInitialValue = Math.ceil(totalCount / itemsPerPage)
  const totalPages = totalCount < itemsPerPage ? 1 : pagesInitialValue
  const onClickPage = (newPage: number) => {
    if (newPage !== currentPage) {
      setoffset(offset ? itemsPerPage * (newPage - 1) : itemsPerPage)
      setCurrentPage(newPage)
      navigate(`/dashboard/terminals/${newPage}`)
    }
  }
  const onItemsPerPageChange = (n: number) => setItemsPerPage(n)

  //Sortings in action bar
  const onFilterName = () => {
    if (sortAsc) {
      setSortingId(TerminalSortings.terminalNameAsc)
    } else {
      setSortingId(TerminalSortings.terminalNameDesc)
    }
    setSortAsc(!sortAsc)
  }
  const onFilterSerialNumber = () => {
    if (sortAsc) {
      setSortingId(TerminalSortings.serialNumberAsc)
    } else {
      setSortingId(TerminalSortings.serialNumberDesc)
    }
    setSortAsc(!sortAsc)
  }
  const onFilterSuborg = () => {
    if (sortAsc) {
      setSortingId(TerminalSortings.subOrganizationAsc)
    } else {
      setSortingId(TerminalSortings.subOrganizationDesc)
    }
    setSortAsc(!sortAsc)
  }
  const onFilterDatausage = () => {
    if (sortAsc) {
      setSortingId(TerminalSortings.dataUsageAsc)
    } else {
      setSortingId(TerminalSortings.dataUsageDesc)
    }
    setSortAsc(!sortAsc)
  }
  const onSortGroup = () => {
    if (sortAsc) {
      setSortingId("groupAsc")
    } else {
      setSortingId("groupDesc")
    }
    setSortAsc(!sortAsc)
  }
  const onSortStatus = () => {
    console.log("sort status")
  }

  const onSetSearchValue = (e: string) => {
    setSearchValue(e)
    onClickPage(1)
  }

  const onSetSorting = (id: Key) => {
    setSortingId(id)
    onClickPage(1)
  }

  React.useEffect(() => {
    document.title = "Dashboard"
  }, [])

  const selectModeEnabled = true
  const [selectedTerminals, setSelectedTerminals] = React.useState<Record<PropertyKey, boolean>>({})
  const selectedTerminalIds = React.useMemo(
    () =>
      Object.entries(selectedTerminals)
        .filter(([, selected]) => selected)
        .map(([key]) => parseInt(key)),
    [selectedTerminals],
  )
  const selectTerminalId = (terminalId: string | number, isSelected: boolean) =>
    setSelectedTerminals((selected) => ({
      ...selected,
      [terminalId]: isSelected,
    }))
  const isTerminalSelected = (terminalId: string | number) => selectedTerminals[terminalId]

  const exportOptions = terminalReportOptions(selectedTerminalIds)

  const noResults = terminals.length < 1
  const noSearch = searchValue === undefined || searchValue === ""
  const noFilter = suborgFilter === undefined || suborgFilter.length < 1

  return (
    <Container>
      <Row justify="end">
        <Col xs="content">
          <Col xs="content">
            <Row>
              <PrimaryButton
                title={selectedTerminalIds.length == 0 ? "Select all" : `Deselect all ${selectedTerminalIds.length}`}
                onClick={() =>
                  selectedTerminalIds.length == 0
                    ? setSelectedTerminals(Object.fromEntries(terminals.map((terminal) => [terminal.id, true])))
                    : setSelectedTerminals({})
                }
              />
              <Spacer spacing={1} />
              {selectedTerminalIds.length > 0 ? (
                <ViewReportButton options={exportOptions} />
              ) : (
                <PrimaryButton disabled title={"View report"} />
              )}
            </Row>
          </Col>
        </Col>
      </Row>
      <HorizontalSpacer spacing={1} />
      <Row>
        <Col lg={6}>
          <Row align="end">
            <Col lg={4}>
              <SearchField onChange={onSetSearchValue} />
              <Visible xs md>
                <HorizontalSpacer spacing={4} />
              </Visible>
            </Col>
            <Col lg={4} md={6} xs={6}>
              <Dropdown
                label={t("sort_by")}
                options={DROPDOWN_OPTIONS}
                onSelectionChange={(id) => onSetSorting(id)}
                selectedKey={sortingId}
              />
            </Col>
            <Col lg={4} md={4} xs={5} style={{ display: "flex" }}>
              <div style={{ display: "flex" }}>
                <Button
                  iconLeft={
                    <ListIcon small color={cardsOpen.length < 1 ? theme.colors.white : theme.colors.greyDark} />
                  }
                  onPress={onClickListView}
                  variant={cardsOpen.length < 1 ? "dark" : "light"}
                />
                <Spacer spacing={1} />
                <Visible xs md>
                  <Spacer spacing={1} />
                </Visible>
                <Button
                  onPress={onClickDetailsView}
                  variant={cardsOpen.length === allTerminalIds.length ? "dark" : "light"}
                  iconLeft={
                    <DetailsIcon
                      small
                      color={cardsOpen.length === allTerminalIds.length ? theme.colors.white : theme.colors.greyDark}
                    />
                  }
                />
              </div>
            </Col>
          </Row>
        </Col>
        <Hidden xs md>
          <Col lg={6}>
            <Row justify="end" nogutter>
              <Link to="/dashboard">
                <PrimaryButton title="Data plans" />
              </Link>
              <Spacer spacing={1} />
              <Link to="/dashboard/groups">
                <PrimaryButton title="Groups" />
              </Link>
              <Spacer spacing={1} />
              <SecondaryButton title="Terminals" />
            </Row>
          </Col>
        </Hidden>
      </Row>
      <Visible xs md>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <HorizontalSpacer spacing={2} />
          <Link to="/dashboard">
            <PrimaryButton title="Data plans" fullWidth />
          </Link>
          <HorizontalSpacer spacing={2} />

          <Link to="/dashboard/groups">
            <PrimaryButton title="Groups" fullWidth />
          </Link>
          <HorizontalSpacer spacing={2} />

          <SecondaryButton title="Terminals" fullWidth />
        </div>
      </Visible>
      <HorizontalSpacer spacing={3} />
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <div>
          {noResults && noFilter && noSearch ? (
            <EmptyDashboardPage />
          ) : noResults ? (
            <NoSearchResultPage />
          ) : (
            <TerminalsOverviewCard
              terminals={terminals}
              onSortTerminalName={onFilterName}
              onSortSerialNumber={onFilterSerialNumber}
              onSortSuborg={onFilterSuborg}
              onSortDataUsage={onFilterDatausage}
              checkCardOpen={checkCardIsOpen}
              onClickCard={onClickCard}
              onSortGroup={onSortGroup}
              onSortStatus={onSortStatus}
              onSelect={selectTerminalId}
              isSelected={isTerminalSelected}
              selectMode={selectModeEnabled}
              onCloseCard={onCloseCard}
            />
          )}
        </div>
      )}
      <HorizontalSpacer spacing={3} />
      {terminals?.length > 0 && (
        <PaginationContainer>
          <PaginationComponent
            pages={totalPages}
            currentPageIndex={currentPage}
            onClickPage={onClickPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={onItemsPerPageChange}
            itemsPerPageValues={PAGINATION_VALUE_PRESETS}
          />
        </PaginationContainer>
      )}
      <HorizontalSpacer spacing={3} />
    </Container>
  )
}

export default TerminalsPage
